/**
 * Turns an unknown thrown value into a user-facing message. Prefers the
 * service-api error envelope (`{ body: { error } }`), then a plain `Error`
 * message, then the caller-supplied fallback.
 *
 * Prefer `describeError` for anything user-facing: it localizes the stable
 * business `code` via i18n. `readableError` stays for callers without a
 * translator and as the final fallback.
 */
export function readableError(error: unknown, fallback = 'request_failed'): string {
  const body = error && typeof error === 'object' && 'body' in error ? (error as { body: unknown }).body : undefined;
  if (body && typeof body === 'object' && 'error' in body) return String((body as { error: unknown }).error);
  return error instanceof Error ? error.message : fallback;
}

/** Minimal shape of react-i18next's `t`, kept structural to avoid TFunction
 *  generic friction. */
type Translate = (key: string, options: Record<string, unknown>) => string;

interface ErrorEnvelope {
  error?: unknown;
  message?: unknown;
  details?: unknown;
}

/**
 * Localizes a service-api error for display. The service-api error envelope is
 * `{ error: <code>, message, details? }`; we map the stable `code` to a
 * localized business message via the `errors.codes.*` i18n namespace, passing
 * `details` through for interpolation (e.g. the offending model id / roles).
 *
 * Resolution order:
 *  1. `errors.codes.<code>` if the code is specific and has a translation.
 *  2. the raw code, when it is specific but has no translation yet (better than
 *     a generic fallback — at least names the failure).
 *  3. the backend `message`, then the `Error` message, then `fallbackKey`.
 */
export function describeError(error: unknown, t: Translate, fallbackKey = 'errors.requestFailed'): string {
  const body = error && typeof error === 'object' && 'body' in error ? (error as { body: unknown }).body : undefined;
  const envelope: ErrorEnvelope | undefined = body && typeof body === 'object' ? (body as ErrorEnvelope) : undefined;
  const code = typeof envelope?.error === 'string' ? envelope.error : error instanceof Error && /^session_[a-z_]+$/.test(error.message) ? error.message : undefined;

  if (code && code !== 'bad_request') {
    const details =
      envelope?.details && typeof envelope.details === 'object' ? (envelope.details as Record<string, unknown>) : {};
    // Pass `details` via i18next's dedicated `replace` bag rather than spreading
    // it into the options object, so a detail key never collides with a reserved
    // i18next option (`count` → pluralization, `context`, `lng`, `ns`, …).
    const localized = t(`errors.codes.${code}`, {
      defaultValue: '',
      replace: details,
      // Error detail keys can legitimately be absent during a rolling deploy
      // where admin-web is newer than service-api. Keep that compatibility
      // behavior local to errors.codes.* so unrelated UI strings still expose
      // missing interpolation values during development and testing.
      missingInterpolationHandler: () => '',
    });
    return localized || code;
  }

  const message = typeof envelope?.message === 'string' ? envelope.message : undefined;
  if (message && message !== 'bad_request') return message;
  return error instanceof Error && error.message ? error.message : t(fallbackKey, {});
}
